<template>
  <ion-toolbar>
    <ion-title></ion-title>
  </ion-toolbar>

  <div class="main_wrapper ion-padding">
    <div class="container container-fill-height">
      <div class="auth-header">
        <div class="auth-heading">
          <h1>Internal Pool Invitation</h1>
        </div>
      </div>

      <div class="job_info_wrapper job_info_wrapper--custom">
        <div class="job_shifts_wrapper fullp">
          <div>
            <p>Hi,</p>
            <p>
              You’ve been invited to join the <b>{{ poolList }}</b> on {{ appName }}.
            </p>
          </div>

          <div>
            <p>Joining gives you:</p>
            <ul>
              <li><strong>Priority access to shifts</strong> – be the first to view and accept available work</li>
              <li><strong>Easy schedule management</strong> – see all your upcoming shifts in one place</li>
              <li><strong>Automated admin</strong> – if you’re a contractor, we’ll handle time-sheeting and invoicing for you</li>
            </ul>
          </div>

          <!-- Pending Group Manager / Onboarding Documents Checklist Card -->
          <div class="pending-documents-card mb-3" v-if="pendingDocumentList && pendingDocumentList.length > 0">
            <div class="pending-documents-header">
              <div class="pending-documents-title">
                <i class="el-icon-document-checked mr-1"></i> Required Onboarding Documents
              </div>
              <div class="pending-documents-subtitle">
                Please review and acknowledge each document before joining the pool.
              </div>
            </div>
            <div class="pending-documents-list">
              <div 
                v-for="(doc, index) in pendingDocumentList" 
                :key="doc.document_id || doc.id || index"
                class="pending-doc-item"
                :class="{ 'is-acknowledged': doc.acknowledged, 'is-required': doc.required }"
                @click="handleDocItemClick(doc)"
              >
                <!-- Left Status Check Icon Indicator -->
                <div 
                  class="doc-status-indicator" 
                  :class="{ 
                    'is-accepted': doc.acknowledged, 
                    'is-pdf': isPdfDoc(doc.url || doc.extension) && !doc.acknowledged, 
                    'is-img': isImageDoc(doc.url || doc.extension) && !doc.acknowledged,
                    'is-pending': !doc.acknowledged && !isPdfDoc(doc.url || doc.extension) && !isImageDoc(doc.url || doc.extension)
                  }"
                >
                  <i v-if="doc.acknowledged" class="el-icon-check"></i>
                  <i v-else-if="isPdfDoc(doc.url || doc.extension)" class="el-icon-document"></i>
                  <i v-else-if="isImageDoc(doc.url || doc.extension)" class="el-icon-picture-outline"></i>
                  <i v-else class="el-icon-document"></i>
                </div>

                <!-- Document Info -->
                <div class="pending-doc-info">
                  <div class="pending-doc-name-row">
                    <span class="pending-doc-name">{{ doc.name }}</span>
                    <span v-if="doc.required && !doc.acknowledged" class="doc-badge-required">Required</span>
                    <span v-if="doc.acknowledged" class="doc-badge-accepted">
                      <i class="el-icon-check"></i> Accepted
                    </span>
                  </div>

                  <div class="pending-doc-meta-row">
                    <span v-if="doc.group_name" class="meta-tag meta-group">
                      <i class="el-icon-office-building"></i> {{ doc.group_name }}
                    </span>
                    <span v-if="!doc.acknowledged" class="doc-action-hint">
                      <i class="el-icon-edit-outline"></i> Click to acknowledge
                    </span>
                  </div>
                </div>

                <!-- Right Action Button -->
                <div class="pending-doc-actions" @click.stop>
                  <button 
                    type="button" 
                    class="btn-doc-small"
                    :class="doc.acknowledged ? 'btn-view' : 'btn-review'"
                    @click="handleDocItemClick(doc)"
                  >
                    <i :class="doc.acknowledged ? 'el-icon-view' : 'el-icon-edit-outline'"></i>
                    <span>{{ doc.acknowledged ? 'View' : 'Acknowledge' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Buttons -->
          <div class="button_submit mb0">
            <div class="btns-group">
              <el-button
                class="submit_page w-full btn-accept"
                type="blue"
                :disabled="!areAllRequiredDocsAccepted || isAccepting"
                :loading="isAccepting"
                @click="gotToAccept"
              >
                Accept
              </el-button>
              <el-button
                class="submit_page w-full secondary"
                type="red"
                :disabled="isRejecting || isAccepting"
                :loading="isRejecting"
                @click="gotToReject"
              >
                Decline
              </el-button>
            </div>
          </div>

          <!-- Support Email -->
          <div class="mt-4 text-center">
            <p>
              Need help? Contact
              <a :href="`mailto:${supportEmail}`" class="support-link">{{ supportEmail }}</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Document Review and Acknowledge Dialog Modal -->
  <el-dialog
    v-model="showDocReviewModal"
    :title="'Review & Acknowledge Document'"
    custom-class="doc-review-dialog"
    width="90%"
    :append-to-body="true"
    :close-on-click-modal="false"
    center
  >
    <div class="doc-modal-content" v-if="selectedDocForReview">
      <div class="doc-modal-header-section">
        <div class="doc-modal-title-row">
          <h3 class="doc-modal-doc-title">
            <i class="el-icon-document mr-1"></i> {{ selectedDocForReview.name }}
          </h3>
          <span v-if="selectedDocForReview.required && !selectedDocForReview.acknowledged" class="doc-badge-required">Required</span>
          <span v-if="selectedDocForReview.acknowledged" class="doc-badge-accepted">
            <i class="el-icon-check"></i> Accepted
          </span>
        </div>

        <div class="doc-modal-actions-toolbar" v-if="selectedDocForReview.url && selectedDocForReview.url !== '='">
          <a 
            :href="selectedDocForReview.url" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="btn-modal-action btn-open"
          >
            <i class="el-icon-top-right"></i> Open in New Tab
          </a>
          <button 
            type="button" 
            class="btn-modal-action btn-download"
            :disabled="isDownloadingDoc"
            @click="downloadDoc(selectedDocForReview)"
          >
            <i :class="isDownloadingDoc ? 'el-icon-loading' : 'el-icon-download'"></i>
            {{ isDownloadingDoc ? 'Downloading...' : 'Download Document' }}
          </button>
        </div>
      </div>

      <!-- Inline Document Preview Box -->
      <div class="doc-inline-preview-wrapper" v-if="selectedDocForReview.url && selectedDocForReview.url !== '='">
        <!-- Image Preview -->
        <div v-if="isImageDoc(selectedDocForReview.url)" class="doc-preview-container doc-preview-image-box">
          <img 
            :src="selectedDocForReview.url" 
            :alt="selectedDocForReview.name" 
            class="doc-preview-image" 
          />
        </div>

        <!-- PDF / Frame Preview -->
        <div v-else-if="isPdfDoc(selectedDocForReview.url)" class="doc-preview-container doc-preview-frame-box">
          <iframe 
            :src="selectedDocForReview.url" 
            class="doc-preview-iframe"
            frameborder="0"
          ></iframe>
        </div>

        <!-- Other File Types Preview -->
        <div v-else class="doc-preview-container doc-preview-frame-box">
          <iframe 
            :src="selectedDocForReview.url" 
            class="doc-preview-iframe"
            frameborder="0"
          ></iframe>
        </div>
      </div>

      <div class="doc-modal-acknowledgement-box">
        <div class="ack-note">
          <i class="el-icon-info mr-1"></i> By clicking <strong>"Acknowledge"</strong>, you confirm that you have read, reviewed, and agreed to the contents of this document.
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer flex-footer">
        <el-button @click="showDocReviewModal = false" class="el-button--grey">Cancel</el-button>
        <el-button 
          type="primary" 
          class="el-button--blue"
          :loading="isAcknowledgingDoc"
          :disabled="selectedDocForReview && selectedDocForReview.acknowledged"
          @click="confirmAcknowledgeDoc"
        >
          {{ (selectedDocForReview && selectedDocForReview.acknowledged) ? 'Already Acknowledged' : 'Acknowledge' }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, computed, onMounted, inject } from "vue";
import { ElMessage, ElMessageBox } from "@/components/Vendor/Element";
import { addIcons } from "ionicons";
import { arrowBackOutline } from "ionicons/icons";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";

addIcons({ "arrow-back-outline": arrowBackOutline });

export default {
  setup() {
    const $store = useStore();
    const $router = useRouter();
    const route = useRoute();
    const appName = process.env.VUE_APP_APP_NAME;
    const supportEmail = process.env.VUE_APP_SUPPORT_EMAIL;
    const setPageClassGlobal = inject("setPageClassGlobal");

    const poolList = ref("");
    const pendingDocumentList = ref([]);
    const showDocReviewModal = ref(false);
    const selectedDocForReview = ref(null);
    const isAcknowledgingDoc = ref(false);
    const isDownloadingDoc = ref(false);
    const isLoadingDocs = ref(true);
    const isAccepting = ref(false);
    const isRejecting = ref(false);

    const formatDocument = (doc, idx = 0, defaultGroupName = "") => {
      if (!doc || typeof doc !== "object") return null;

      const isAck =
        doc.acknowledged === true ||
        doc.acknowledged === 1 ||
        doc.acknowledged === "1" ||
        doc.accepted === true ||
        doc.accepted === 1 ||
        doc.status === "accepted" ||
        doc.acknowledgement_status === "accepted";

      const isReq =
        doc.required === true ||
        doc.required === 1 ||
        doc.required === "1" ||
        doc.is_required === true ||
        doc.is_required === 1 ||
        doc.is_required === "1" ||
        doc.mandatory === true ||
        doc.is_mandatory === true;

      const docName =
        doc.document_name ||
        doc.name ||
        doc.title ||
        doc.file_name ||
        (doc.url && doc.url.includes("/")
          ? decodeURIComponent(doc.url.split("?")[0].split("/").pop())
              .replace(/\.[^/.]+$/, "")
              .replace(/[-_]/g, " ")
              .trim()
          : "") ||
        "Onboarding Document";

      return {
        ...doc,
        id: doc.document_id || doc.id || `doc_${idx}`,
        document_id: doc.document_id || doc.id,
        group_user_id: doc.group_user_id,
        name: docName,
        group_name: doc.group_name || defaultGroupName || "",
        extension: doc.extension || doc.ext || "",
        required: isReq,
        url: doc.url || doc.file_url || doc.document_url || "",
        status: isAck ? "accepted" : (doc.status || "pending"),
        acknowledged: isAck,
      };
    };

    const areAllRequiredDocsAccepted = computed(() => {
      if (isLoadingDocs.value) {
        return false;
      }
      if (!pendingDocumentList.value || pendingDocumentList.value.length === 0) {
        return true;
      }
      const requiredDocs = pendingDocumentList.value.filter((doc) => doc.required);
      if (requiredDocs.length > 0) {
        return requiredDocs.every((doc) => doc.acknowledged);
      }
      return pendingDocumentList.value.every((doc) => doc.acknowledged);
    });

    const handleDocItemClick = (doc) => {
      selectedDocForReview.value = doc;
      showDocReviewModal.value = true;
    };

    const isImageDoc = (url) => {
      if (!url) return false;
      const cleanUrl = url.split("?")[0].toLowerCase();
      return /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(cleanUrl);
    };

    const isPdfDoc = (url) => {
      if (!url) return false;
      const cleanUrl = url.split("?")[0].toLowerCase();
      return /\.pdf$/i.test(cleanUrl);
    };

    const downloadDoc = async (doc) => {
      if (!doc || !doc.url || doc.url === "=") return;
      try {
        isDownloadingDoc.value = true;

        let extension = "";
        try {
          const path = doc.url.split("?")[0];
          const parts = path.split(".");
          if (parts.length > 1) {
            extension = "." + parts.pop().toLowerCase();
          }
        } catch (e) {
          // ignore
        }

        let filename = doc.name ? doc.name.trim() : "onboarding-document";
        if (extension && !filename.toLowerCase().endsWith(extension)) {
          filename += extension;
        } else if (!extension) {
          if (isPdfDoc(doc.url)) filename += ".pdf";
          else if (isImageDoc(doc.url)) filename += ".png";
        }

        let downloaded = false;

        // Method 1: Fetch API Blob
        try {
          const response = await fetch(doc.url, { mode: "cors" });
          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.style.display = "none";
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(blobUrl);
            }, 300);
            downloaded = true;
          }
        } catch (fetchErr) {
          console.warn("Fetch blob download failed, trying XHR:", fetchErr);
        }

        // Method 2: XHR Blob
        if (!downloaded) {
          try {
            await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open("GET", doc.url, true);
              xhr.responseType = "blob";
              xhr.onload = function () {
                if (this.status === 200 || this.status === 0) {
                  const blobUrl = window.URL.createObjectURL(this.response);
                  const link = document.createElement("a");
                  link.style.display = "none";
                  link.href = blobUrl;
                  link.download = filename;
                  document.body.appendChild(link);
                  link.click();
                  setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                    resolve(true);
                  }, 300);
                } else {
                  reject(new Error("XHR status " + this.status));
                }
              };
              xhr.onerror = reject;
              xhr.send();
            });
            downloaded = true;
          } catch (xhrErr) {
            console.warn("XHR blob download failed, trying Canvas / Anchor:", xhrErr);
          }
        }

        // Method 3: Canvas for images
        if (!downloaded && isImageDoc(doc.url)) {
          try {
            await new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = "anonymous";
              img.onload = () => {
                try {
                  const canvas = document.createElement("canvas");
                  canvas.width = img.naturalWidth || img.width;
                  canvas.height = img.naturalHeight || img.height;
                  const ctx = canvas.getContext("2d");
                  ctx.drawImage(img, 0, 0);
                  canvas.toBlob((blob) => {
                    if (blob) {
                      const blobUrl = window.URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.style.display = "none";
                      link.href = blobUrl;
                      link.download = filename;
                      document.body.appendChild(link);
                      link.click();
                      setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                        resolve(true);
                      }, 300);
                    } else {
                      reject(new Error("Canvas blob is null"));
                    }
                  }, "image/png");
                } catch (err) {
                  reject(err);
                }
              };
              img.onerror = reject;
              img.src = doc.url;
            });
            downloaded = true;
          } catch (canvasErr) {
            console.warn("Canvas download failed, falling back to anchor:", canvasErr);
          }
        }

        // Method 4: Hidden anchor fallback
        if (!downloaded) {
          const link = document.createElement("a");
          link.style.display = "none";
          link.href = doc.url;
          link.setAttribute("download", filename);
          link.setAttribute("target", "_self");
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
          }, 300);
        }

        ElMessage({
          type: "success",
          message: `Downloading "${filename}"...`,
        });
      } catch (err) {
        console.error("Download error:", err);
        ElMessage({
          type: "error",
          message: "Could not download document.",
        });
      } finally {
        isDownloadingDoc.value = false;
      }
    };

    const confirmAcknowledgeDoc = async () => {
      if (!selectedDocForReview.value) return;
      try {
        isAcknowledgingDoc.value = true;
        const payload = {
          document_id: selectedDocForReview.value.document_id || selectedDocForReview.value.id,
          status: "accepted",
        };
        if (selectedDocForReview.value.group_user_id) {
          payload.group_user_id = selectedDocForReview.value.group_user_id;
        }
        if (route.params.invitationId) {
          payload.invitation_id = route.params.invitationId;
        }

        await $store.dispatch("ACKNOWLEDGE_ONBOARDING_DOCUMENT", payload);

        selectedDocForReview.value.acknowledged = true;
        selectedDocForReview.value.status = "accepted";

        const targetId = selectedDocForReview.value.document_id || selectedDocForReview.value.id;
        pendingDocumentList.value.forEach((d) => {
          if (
            (d.document_id && d.document_id === targetId) ||
            (d.id && d.id === targetId) ||
            d.name === selectedDocForReview.value.name
          ) {
            d.acknowledged = true;
            d.status = "accepted";
          }
        });

        ElMessage({
          type: "success",
          message: `${selectedDocForReview.value.name} acknowledged successfully!`,
        });

        showDocReviewModal.value = false;
      } catch (err) {
        console.error("Acknowledge document error:", err);
        ElMessage({
          type: "error",
          message: (err && err.message) || "Failed to acknowledge document. Please try again.",
        });
      } finally {
        isAcknowledgingDoc.value = false;
      }
    };

    // ❌ Handle decline
    const gotToReject = async () => {
      if (isRejecting.value) return;
      isRejecting.value = true;
      try {
        await $store.dispatch("INTERNAL_POOL_REJECT", {
          invitationId: route.params.invitationId,
        });

        await ElMessageBox.confirm("You have declined", "Internal Pool Declined!", {
          showCancelButton: false,
          confirmButtonText: "Sign up to Locumate",
          confirmButtonClass: "el-button--grey",
          showClose: true,
          type: "warning",
          customClass: "modal_success star declined-invitation",
          callback: () => $router.push({ name: "auth" }),
        });
      } catch (error) {
        console.error(error);
      } finally {
        isRejecting.value = false;
      }
    };

    // ✅ Handle accept
    const gotToAccept = async () => {
      if (!areAllRequiredDocsAccepted.value || isAccepting.value) {
        ElMessage({
          type: "warning",
          message: "Please review and acknowledge all required documents before accepting.",
        });
        return;
      }

      isAccepting.value = true;
      try {
        await $store.dispatch("INTERNAL_POOL_ACCEPT", {
          invitationId: route.params.invitationId,
        });

        await ElMessageBox.confirm(
          `You’ve successfully joined the ${poolList.value}. You’ll now receive priority notifications for available shifts.`,
          "You’re In!",
          {
            showCancelButton: false,
            confirmButtonText: "Ok",
            confirmButtonClass: "el-button--grey",
            type: "success",
            customClass: "modal_success star",
            callback: () => $router.push({ name: "home" }),
          }
        );
      } catch (error) {
        console.error(error);
      } finally {
        isAccepting.value = false;
      }
    };

    // 🚀 Lifecycle
    onMounted(async () => {
      if (setPageClassGlobal) setPageClassGlobal("global_job_book_page");

      try {
        const internalPoolInfo = await $store.dispatch("GET_INTERNAL_POOL_INFO", {
          invitationId: route.params.invitationId,
        });

        if (!internalPoolInfo || internalPoolInfo.status === 500 || internalPoolInfo.statusCode === 500 || internalPoolInfo.error) {
          $router.replace({ name: "internal_pool_unavailable" });
          return;
        }

        const invitation = internalPoolInfo?.invitation;
        const accepted = !!(invitation?.acceptedAt || invitation?.accepted_at);
        const rejected = !!(invitation?.rejectedAt || invitation?.rejected_at);
        const isVerified =
          internalPoolInfo?.verified == true ||
          internalPoolInfo?.verified === "true" ||
          internalPoolInfo?.verified === 1 ||
          internalPoolInfo?.is_verified == true ||
          internalPoolInfo?.is_verified === "true" ||
          internalPoolInfo?.is_verified === 1 ||
          invitation?.verified == true ||
          invitation?.verified === "true" ||
          invitation?.verified === 1 ||
          invitation?.is_verified == true ||
          invitation?.is_verified === "true" ||
          invitation?.is_verified === 1;

        if (internalPoolInfo?.verified == true || isVerified || accepted || rejected) {
          $router.replace({ name: "internal_pool_unavailable" });
          return;
        }

        const poolData = internalPoolInfo.internalPoolList || internalPoolInfo.internal_pool_list;
        poolList.value =
          poolData?.internalpoollistable?.name ||
          poolData?.displayName ||
          poolData?.display_name ||
          "";

        // If invitation has empty document list, do not show onboarding documents
        if (Array.isArray(invitation?.document) && invitation.document.length === 0) {
          pendingDocumentList.value = [];
          isLoadingDocs.value = false;
          return;
        }

        // Fetch onboarding documents from Group Manager Documents API
        try {
          const groupId = poolData?.internalpoollistable_id || poolData?.internalpoollistableId || poolData?.internalpoollistable?.id;
          const docRes = await $store.dispatch("GET_GROUP_MANAGER_DOCUMENTS", {
            invitation_id: route.params.invitationId,
            group_id: groupId,
            pharmacy_group_id: groupId,
            internal_pool_list_id: poolData?.id,
          });

          let docList = [];
          if (Array.isArray(docRes?.documents)) {
            docList = docRes.documents;
          } else if (Array.isArray(docRes?.data?.documents)) {
            docList = docRes.data.documents;
          } else if (Array.isArray(docRes?.data)) {
            docList = docRes.data;
          } else if (Array.isArray(docRes)) {
            docList = docRes;
          } else if (Array.isArray(invitation?.documents)) {
            docList = invitation.documents;
          } else if (Array.isArray(invitation?.document)) {
            docList = invitation.document;
          }

          pendingDocumentList.value = docList
            .map((doc, idx) => formatDocument(doc, idx, poolList.value))
            .filter(Boolean);
        } catch (docErr) {
          console.warn("Group manager documents fetch notice:", docErr);
          pendingDocumentList.value = [];
        } finally {
          isLoadingDocs.value = false;
        }
      } catch (error) {
        console.error("Internal pool init error (500 or network fail):", error);
        $router.replace({ name: "internal_pool_unavailable" });
      } finally {
        isLoadingDocs.value = false;
      }
    });

    return {
      gotToAccept,
      gotToReject,
      poolList,
      appName,
      supportEmail,
      pendingDocumentList,
      showDocReviewModal,
      selectedDocForReview,
      isAcknowledgingDoc,
      isDownloadingDoc,
      areAllRequiredDocsAccepted,
      handleDocItemClick,
      confirmAcknowledgeDoc,
      downloadDoc,
      isImageDoc,
      isPdfDoc,
      isLoadingDocs,
      isAccepting,
      isRejecting,
    };
  },
};
</script>

<style scoped>
/* Onboarding Documents Checklist Styles */
.pending-documents-card {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-top: 18px;
  margin-bottom: 18px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.pending-documents-header {
  margin-bottom: 12px;
  text-align: left;
}

.pending-documents-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pending-documents-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 3px;
  line-height: 1.35;
}

.pending-documents-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pending-doc-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  user-select: none;
}

.pending-doc-item:hover {
  border-color: #3b82f6;
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.12);
  transform: translateY(-1px);
}

.pending-doc-item.is-acknowledged {
  background: #f0fdf4;
  border-color: #86efac;
}

/* Status Indicator Circle Icon */
.doc-status-indicator {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.doc-status-indicator.is-accepted {
  background: #16a34a;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(22, 163, 74, 0.3);
}

.doc-status-indicator.is-accepted i {
  font-weight: 700;
  font-size: 18px;
}

.doc-status-indicator.is-pending {
  background: #f1f5f9;
  color: #64748b;
  border: 1.5px solid #cbd5e1;
}

.doc-status-indicator.is-pdf {
  background: #fef2f2;
  color: #ef4444;
  border: 1.5px solid #fecaca;
}

.doc-status-indicator.is-img {
  background: #f0fdf4;
  color: #10b981;
  border: 1.5px solid #bbf7d0;
}

.pending-doc-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.pending-doc-name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.pending-doc-name {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.doc-badge-required {
  background: #fee2e2;
  color: #dc2626;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9999px;
  letter-spacing: 0.2px;
}

.doc-badge-accepted {
  background: #dcfce7;
  color: #15803d;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pending-doc-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.meta-tag {
  font-size: 11px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.meta-tag.meta-ext {
  background: #e0f2fe;
  color: #0284c7;
  font-weight: 700;
  font-size: 10px;
}

.meta-tag.meta-size {
  background: #f1f5f9;
  color: #64748b;
}

.meta-tag.meta-group {
  background: #f3f4f6;
  color: #4b5563;
}

.doc-action-hint {
  font-size: 11.5px;
  color: #3b82f6;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.pending-doc-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.btn-doc-small {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  white-space: nowrap;
}

.btn-doc-small.btn-review {
  background: #2563eb;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(37, 99, 235, 0.25);
}

.btn-doc-small.btn-review:hover {
  background: #1d4ed8;
}

.btn-doc-small.btn-view {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.btn-doc-small.btn-view:hover {
  background: #dbeafe;
}

/* Document Review Modal Dialog Styles */
:deep(.doc-review-dialog) {
  border-radius: 16px;
  overflow: hidden;
  max-width: 680px;
}

.doc-modal-content {
  text-align: left;
}

.doc-modal-header-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 14px;
}

.doc-modal-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.doc-modal-doc-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
}

.doc-modal-actions-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-modal-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-modal-action.btn-open {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.btn-modal-action.btn-open:hover {
  background: #dbeafe;
  color: #1d4ed8;
}

.btn-modal-action.btn-download {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.btn-modal-action.btn-download:hover:not(:disabled) {
  background: #dcfce7;
  color: #15803d;
}

.btn-modal-action.btn-download:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Document Inline Preview */
.doc-inline-preview-wrapper {
  margin: 12px 0;
}

.doc-preview-container {
  width: 100%;
  border-radius: 10px;
  background: #f8fafc;
  border: 1.5px solid #cbd5e1;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.doc-preview-image-box {
  max-height: 440px;
  padding: 12px;
  background: #0f172a;
}

.doc-preview-image {
  max-width: 100%;
  max-height: 410px;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.doc-preview-frame-box {
  height: 420px;
}

.doc-preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.doc-modal-acknowledgement-box {
  background: #f8fafc;
  border: 1px dashed #94a3b8;
  border-radius: 8px;
  padding: 12px;
  margin-top: 14px;
}

.ack-note {
  font-size: 12px;
  color: #334155;
  line-height: 1.4;
}

.flex-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Invitation Accept / Decline Action Buttons */
.button_submit {
  .btns-group {
    .el-button.submit_page {
      &.btn-accept {
        background-color: #2563eb;
        border-color: #2563eb;
        color: #ffffff;
        transition: all 0.2s ease;

        &:hover:not(.is-disabled):not(:disabled) {
          background-color: #1d4ed8;
          border-color: #1d4ed8;
        }

        &.is-disabled,
        &.is-disabled:hover,
        &.is-disabled:focus,
        &:disabled,
        &:disabled:hover {
          background-color: #93c5fd !important;
          border-color: #93c5fd !important;
          color: #ffffff !important;
          cursor: not-allowed !important;
          opacity: 0.65 !important;
        }
      }
    }
  }
}
</style>
